export type FormFieldValidator<T> = (value: T, initialValue: T) => string | null;
export type FormFieldAsyncValidator<T> = (value: T, initialValue: T) => Promise<string | null>;

export const FIELD_ERROR_PLACEHOLDER = '[field]';

export const validateRequired = <T = unknown>(
  val: T,
  _initialVal: T
): ReturnType<FormFieldValidator<T>> => {
  if (typeof val === 'string') {
    if (!val.trim().length) {
      return `${FIELD_ERROR_PLACEHOLDER} is required`;
    }
    return null;
  }
  if (typeof val === 'number') {
    if (val <= 0) {
      return `${FIELD_ERROR_PLACEHOLDER} must be greater than zero`;
    }
    return null;
  }
  if (Array.isArray(val)) {
    if (!val.length) {
      return `${FIELD_ERROR_PLACEHOLDER} must not be empty`;
    }
    return null;
  }
  if (!(typeof val === 'boolean') && !val) {
    return `${FIELD_ERROR_PLACEHOLDER} is required`;
  }
  return null;
};

export const validateMinLength = <T = unknown>(minLengthParam?: number): FormFieldValidator<T> => {
  const minLength = Number(minLengthParam);

  return (val: T, _initialVal: T) => {
    if (Number.isNaN(minLength) || minLength < 0) {
      return null;
    }

    if (typeof val === 'string') {
      if (val.trim().length < minLength) {
        return `${FIELD_ERROR_PLACEHOLDER} must be longer than ${minLength} characters`;
      }
      return null;
    }
    if (typeof val === 'number') {
      if (val < minLength) {
        return `${FIELD_ERROR_PLACEHOLDER} must be greater than ${minLength}`;
      }
      return null;
    }
    if (Array.isArray(val)) {
      if (val.length < minLength) {
        return `${FIELD_ERROR_PLACEHOLDER} must have at least ${minLength} items`;
      }
      return null;
    }
    return null;
  };
};

export const validateMaxLength = <T = unknown>(maxLengthParam?: number): FormFieldValidator<T> => {
  const maxLength = Number(maxLengthParam);
  return (val: T, _initialVal: T) => {
    if (Number.isNaN(maxLength) || maxLength <= 0) {
      return null;
    }

    if (typeof val === 'string') {
      if (val.trim().length > maxLength) {
        return `${FIELD_ERROR_PLACEHOLDER} must be ${maxLength} characters or less`;
      }
      return null;
    }
    if (typeof val === 'number') {
      if (val > maxLength) {
        return `${FIELD_ERROR_PLACEHOLDER} must be ${maxLength} or less`;
      }
      return null;
    }
    if (Array.isArray(val)) {
      if (val.length > maxLength) {
        return `${FIELD_ERROR_PLACEHOLDER} must have ${maxLength} items or less`;
      }
      return null;
    }
    return null;
  };
};
